/**
 * The store the UI holds: the workspace and every room, as one (PLAN-COLLAB
 * C2, C3, D-CO11a).
 *
 * A shared recipe is not in the workspace. It lives in its room's own
 * {@link WorkspaceStore} (`sync/room-sources.ts`), so a screen that reads
 * `state.recipes[id]` and dispatches `recipe.updateHop` has two stores to
 * talk to and must not know it. This class sits between them and the app's
 * `useWorkspaceStore` call sites, which see nothing but {@link WorkspaceStoreApi}.
 *
 * ## Reads
 *
 * `getState()` is `overlaySharedRecipes(workspace, rooms)`: the workspace with
 * each room's recipe and snapshots laid over it and the effective equipment
 * written in (D-CO3). Every selector in `workspace-context.tsx` memoises on the
 * identity of the state it is handed, so the overlay is memoised on the
 * identity of **every** input: an edit to one room, or a presence frame, or a
 * status change, gives back the very object the last call did. With no shared
 * recipes it is the workspace's own state object, untouched.
 *
 * ## Writes
 *
 * Each command goes where `commandRoom` says: the room that holds its recipe,
 * or the workspace. That is the room's own rule (`roomRefusal`), asked the
 * other way round, so nothing is sent to a room that the room would refuse. A
 * batch that spans sources — rare, since a batch is one thought and usually
 * about one document — is cut into consecutive runs, one `dispatchBatch` per
 * run. If a later run is refused the earlier ones are undone, so the batch is
 * still all or nothing, as `dispatchBatch` promises. Undone, not unsent: a
 * rolled-back part that landed in a room leaves the command and its inverse
 * in that room's outbox, and the room applies both, a net no-op.
 *
 * A write to a room is bracketed by `RoomSources.open` and its release,
 * because a room's client only observes its store while it runs, and an
 * unobserved command reaches no outbox.
 *
 * ## One undo stack (D-CO11a)
 *
 * Undo is global — toasts all over the app call `store.undo()` — so it stays
 * one stack here. Each source's store keeps its own inverses and labels each
 * entry with an id; this class keeps the order, as a stack of entries whose
 * parts are `(source, entryId)` pairs. A source's store may drop an entry
 * when another member's edit touches the same field (D-CO10), and an entry
 * with a part that is gone is skipped. Before a part is undone its entry must
 * be the one its store would undo next; an entry of the store's own that this
 * stack never saw (only the sync client dispatches around it) is undone first,
 * as the plain store would have.
 *
 * ## With no rooms
 *
 * A guest has no rooms, and a signed-in account may be in none. Then every
 * member is the workspace store's own, called straight through, and this
 * class adds nothing: no stack, no overlay, no copy.
 */

import type {
  Command,
  CommandOf,
  CommandType,
  RecipeDoc,
  WorkspaceState,
} from '@beerwright/commands';
import { commandRoom, makeCommand, overlaySharedRecipes } from '@beerwright/commands';
import type { RoomSource, RoomSources } from '@/sync/room-sources';
import type {
  DispatchResult,
  DispatchStep,
  HistorySnapshot,
  WorkspaceStore,
} from './workspace-store';

/**
 * The surface the app's `useWorkspaceStore` call sites use;
 * {@link WorkspaceStore} implements it too.
 */
export interface WorkspaceStoreApi {
  readonly getState: () => WorkspaceState;
  readonly getHistory: () => HistorySnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  readonly dispatch: <K extends CommandType>(
    type: K,
    payload: CommandOf<K>['payload'],
    options?: { readonly actor?: 'user' | 'assistant' },
  ) => DispatchResult;
  readonly dispatchAll: (steps: readonly DispatchStep[], summary?: string) => DispatchResult;
  readonly dispatchBatch: (commands: readonly Command[], summary?: string) => DispatchResult;
  readonly undo: () => DispatchResult | null;
  readonly redo: () => DispatchResult | null;
}

/**
 * `next`, with every table and every shared recipe that has not changed since
 * `previous` put back as the object it was.
 *
 * The overlay copies `recipes`, `recipeSnapshots` and `equipmentProfiles`, and
 * spreads each shared recipe to write its effective equipment in, so a
 * workspace edit that touched no recipe (an inventory tick, a setting) would
 * otherwise hand every `useRecipe` of a shared recipe a new object, and every
 * table selector a new table. Shallow per table, so it costs one pass over
 * each table, and only when an input actually moved.
 */
function stabilize(next: WorkspaceState, previous: WorkspaceState | null): WorkspaceState {
  if (previous === null || next === previous) return next;
  const recipes = keepSame(next.recipes, previous.recipes, sameRecipe);
  const recipeSnapshots = keepSame(next.recipeSnapshots, previous.recipeSnapshots, Object.is);
  const equipmentProfiles = keepSame(next.equipmentProfiles, previous.equipmentProfiles, Object.is);
  if (
    recipes === next.recipes &&
    recipeSnapshots === next.recipeSnapshots &&
    equipmentProfiles === next.equipmentProfiles
  ) {
    return next;
  }
  return { ...next, recipes, recipeSnapshots, equipmentProfiles };
}

/** A shared recipe re-spread from the same room document, to the same equipment. */
function sameRecipe(a: RecipeDoc, b: RecipeDoc): boolean {
  if (a === b) return true;
  const { equipmentProfileId: aEquipment, ...aRest } = a;
  const { equipmentProfileId: bEquipment, ...bRest } = b;
  if (aEquipment !== bEquipment) return false;
  const keys = Object.keys(aRest) as (keyof typeof aRest)[];
  return (
    keys.length === Object.keys(bRest).length &&
    keys.every((key) => Object.is(aRest[key], bRest[key]))
  );
}

/**
 * `next` with each value equal to `previous`'s under `same` replaced by
 * `previous`'s, or `previous` itself when every key and value matches.
 */
function keepSame<T>(
  next: Readonly<Record<string, T>>,
  previous: Readonly<Record<string, T>>,
  same: (a: T, b: T) => boolean,
): Readonly<Record<string, T>> {
  if (next === previous) return next;
  const keys = Object.keys(next);
  let all = keys.length === Object.keys(previous).length;
  let copy: Record<string, T> | null = null;
  for (const key of keys) {
    const value = next[key] as T;
    const before = previous[key];
    if (before !== undefined && before !== value && same(value, before)) {
      copy ??= { ...next };
      copy[key] = before;
    } else if (before !== value) {
      all = false;
    }
  }
  if (all) return previous;
  return copy ?? next;
}

/** The workspace's name in a part. `#` is outside the document id alphabet, so no recipe has it. */
const WORKSPACE = '#workspace';

/** How many entries the stack keeps, as each store does. */
const HISTORY_LIMIT = 200;

interface Part {
  readonly source: string;
  readonly entryId: string;
}

interface Entry {
  readonly parts: readonly Part[];
  readonly summary: string | null;
}

const EMPTY_HISTORY: HistorySnapshot = {
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null,
};

export class RoutedWorkspaceStore implements WorkspaceStoreApi {
  readonly #workspace: WorkspaceStore;
  #rooms: RoomSources | null;
  readonly #listeners = new Set<() => void>();
  #unsubscribe: (() => void) | null = null;
  readonly #undo: Entry[] = [];
  readonly #redo: Entry[] = [];
  #merged: {
    readonly workspace: WorkspaceState;
    readonly rooms: Readonly<Record<string, WorkspaceState>>;
    readonly count: number;
    readonly state: WorkspaceState;
  } | null = null;
  #history: HistorySnapshot = EMPTY_HISTORY;

  /**
   * @param rooms - The rooms, or `null` until there is a session to have them;
   *   see {@link RoutedWorkspaceStore.attach}.
   */
  constructor(workspace: WorkspaceStore, rooms: RoomSources | null = null) {
    this.#workspace = workspace;
    this.#rooms = rooms;
  }

  /**
   * Hand over the rooms, or take them back (`null`).
   *
   * The store outlives the session, as the workspace store does, and the rooms
   * do not: they belong to one signed-in account, and the sync provider builds
   * them beside its client.
   */
  attach(rooms: RoomSources | null): void {
    if (rooms === this.#rooms) return;
    const listening = this.#unsubscribe !== null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#rooms = rooms;
    this.#merged = null;
    this.#undo.length = 0;
    this.#redo.length = 0;
    if (listening) this.#listen();
    this.#emit();
  }

  /** True once the room for `recipeId` has caught up at least once (D-CO11c). */
  isRoomSettled(recipeId: string): boolean {
    return this.#rooms?.get(recipeId)?.status === 'ready';
  }

  /** The room store for `recipeId`, for the editor's presence and claims. */
  room(recipeId: string): RoomSource | null {
    return this.#rooms?.get(recipeId) ?? null;
  }

  // -------------------------------------------------------------------------
  // Reads
  // -------------------------------------------------------------------------

  getState = (): WorkspaceState => {
    const workspace = this.#workspace.getState();
    const rooms = this.#rooms;
    if (rooms === null) return workspace;
    const ids = Object.keys(workspace.sharedRecipes);
    if (ids.length === 0) return workspace;

    // Compared before anything is built: this runs on every selector read.
    const last = this.#merged;
    let same = last !== null && last.workspace === workspace;
    let count = 0;
    for (const id of ids) {
      const room = rooms.get(id);
      if (room === undefined) continue;
      count += 1;
      if (same && last?.rooms[id] !== room.store.getState()) same = false;
    }
    if (same && last !== null && last.count === count) return last.state;

    const states = this.#roomStates();
    const state = stabilize(overlaySharedRecipes(workspace, states), last?.state ?? null);
    this.#merged = { workspace, rooms: states, count, state };
    return state;
  };

  getHistory = (): HistorySnapshot => {
    if (this.#plain()) return this.#workspace.getHistory();
    const undo = this.#undoTarget(false);
    const redo = this.#redoTarget(false);
    const next: HistorySnapshot = {
      canUndo: undo !== null,
      canRedo: redo !== null,
      undoLabel: undo?.label ?? null,
      redoLabel: redo?.label ?? null,
    };
    const last = this.#history;
    if (
      last.canUndo === next.canUndo &&
      last.canRedo === next.canRedo &&
      last.undoLabel === next.undoLabel &&
      last.redoLabel === next.redoLabel
    ) {
      return last;
    }
    this.#history = next.canUndo || next.canRedo ? next : EMPTY_HISTORY;
    return this.#history;
  };

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    if (this.#unsubscribe === null) this.#listen();
    return () => {
      this.#listeners.delete(listener);
      if (this.#listeners.size === 0) {
        this.#unsubscribe?.();
        this.#unsubscribe = null;
      }
    };
  };

  // -------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------

  dispatch = <K extends CommandType>(
    type: K,
    payload: CommandOf<K>['payload'],
    options: { readonly actor?: 'user' | 'assistant' } = {},
  ): DispatchResult => {
    if (this.#plainForWrite()) return this.#workspace.dispatch(type, payload, options);
    // Routing reads only the type and the payload, so a probe with a dummy
    // envelope answers it, and the chosen store stamps the real one.
    const probe = makeCommand(type, payload, { commandId: 'probe', timestamp: 0, actor: 'user' });
    const source = this.#sourceOf(probe as Command, this.#roomStates());
    return this.#write([{ source, run: (store) => store.dispatch(type, payload, options) }]);
  };

  dispatchAll = (steps: readonly DispatchStep[], summary?: string): DispatchResult => {
    if (this.#plainForWrite()) return this.#workspace.dispatchAll(steps, summary);
    const roomStates = this.#roomStates();
    const runs = this.#runs(steps, (step) =>
      this.#sourceOf(
        makeCommand(step.type, step.payload, {
          commandId: 'probe',
          timestamp: 0,
          actor: 'user',
        }) as Command,
        roomStates,
      ),
    );
    return this.#write(
      runs.map(({ source, items }) => ({
        source,
        size: items.length,
        run: (store) => store.dispatchAll(items, summary),
      })),
    );
  };

  dispatchBatch = (commands: readonly Command[], summary?: string): DispatchResult => {
    if (this.#plainForWrite()) return this.#workspace.dispatchBatch(commands, summary);
    const roomStates = this.#roomStates();
    const runs = this.#runs(commands, (command) => this.#sourceOf(command, roomStates));
    return this.#write(
      runs.map(({ source, items }) => ({
        source,
        size: items.length,
        run: (store) => store.dispatchBatch(items, summary),
      })),
    );
  };

  undo = (): DispatchResult | null => {
    if (this.#plainForWrite()) return this.#workspace.undo();
    const target = this.#undoTarget(true);
    if (target === null) return null;

    if (target.entry === null) {
      // The store's own entry, which this stack never saw: undone as the plain
      // store would, and remembered so it can be redone.
      const store = target.store;
      const result = store.undo();
      if (result?.ok && result.entryId !== null) {
        this.#redo.push({
          parts: [{ source: target.source, entryId: result.entryId }],
          summary: target.label,
        });
      }
      return this.#result(result);
    }

    const { entry } = target;
    const done: Part[] = [];
    for (const part of [...entry.parts].reverse()) {
      const result = this.#storeOf(part.source)?.undo() ?? null;
      if (result === null || !result.ok) {
        for (const undone of done.reverse()) this.#storeOf(undone.source)?.redo();
        return result;
      }
      done.push(part);
    }
    this.#undo.pop();
    this.#redo.push(entry);
    return { ok: true, state: this.getState(), entryId: entry.parts.at(-1)?.entryId ?? null };
  };

  redo = (): DispatchResult | null => {
    if (this.#plainForWrite()) return this.#workspace.redo();
    const target = this.#redoTarget(true);
    if (target === null) return null;
    const done: Part[] = [];
    for (const part of target.entry.parts) {
      const result = this.#storeOf(part.source)?.redo() ?? null;
      if (result === null || !result.ok) {
        for (const redone of done.reverse()) this.#storeOf(redone.source)?.undo();
        return result;
      }
      done.push(part);
    }
    this.#redo.pop();
    this.#undo.push(target.entry);
    return {
      ok: true,
      state: this.getState(),
      entryId: target.entry.parts.at(-1)?.entryId ?? null,
    };
  };

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  /** No rooms to route to: every member is the workspace's own. A pure read. */
  #plain(): boolean {
    return this.#rooms === null || this.#rooms.all().length === 0;
  }

  /**
   * {@link RoutedWorkspaceStore.#plain}, for the write paths, which also drop
   * the stack when there are no rooms: entries from a time with rooms name
   * stores that are gone, and any of the workspace's own are still on its
   * stack, so the workspace store is the whole of history again. Done here and
   * when the rooms change, never on a read.
   */
  #plainForWrite(): boolean {
    const plain = this.#plain();
    if (plain) this.#dropHistory();
    return plain;
  }

  #dropHistory(): void {
    this.#undo.length = 0;
    this.#redo.length = 0;
  }

  #listen(): void {
    const offWorkspace = this.#workspace.subscribe(this.#emit);
    const offRooms = this.#rooms?.subscribe(() => {
      if (this.#plain()) this.#dropHistory();
      this.#emit();
    });
    this.#unsubscribe = () => {
      offWorkspace();
      offRooms?.();
    };
  }

  #emit = (): void => {
    for (const listener of this.#listeners) listener();
  };

  #roomStates(): Record<string, WorkspaceState> {
    const states: Record<string, WorkspaceState> = {};
    for (const room of this.#rooms?.all() ?? []) states[room.recipeId] = room.store.getState();
    return states;
  }

  /**
   * Where a command goes: a room's recipe id, or {@link WORKSPACE}.
   * `roomStates` is taken once per write, not once per command.
   */
  #sourceOf(command: Command, roomStates: Readonly<Record<string, WorkspaceState>>): string {
    const workspace = this.#workspace.getState();
    const recipeId = commandRoom(workspace, command, roomStates);
    if (recipeId === null || this.#rooms?.get(recipeId) === undefined) return WORKSPACE;
    return recipeId;
  }

  #storeOf(source: string): WorkspaceStore | null {
    if (source === WORKSPACE) return this.#workspace;
    return this.#rooms?.get(source)?.store ?? null;
  }

  /** Cut a list into consecutive runs that go to the same source. */
  #runs<T>(items: readonly T[], sourceOf: (item: T) => string): { source: string; items: T[] }[] {
    const runs: { source: string; items: T[] }[] = [];
    for (const item of items) {
      const source = sourceOf(item);
      const last = runs.at(-1);
      if (last?.source === source) last.items.push(item);
      else runs.push({ source, items: [item] });
    }
    return runs;
  }

  /**
   * Run each part against its store, all or nothing, and stack what landed as
   * one entry.
   */
  #write(
    parts: readonly {
      readonly source: string;
      readonly size?: number;
      readonly run: (store: WorkspaceStore) => DispatchResult;
    }[],
  ): DispatchResult {
    const landed: { source: string; entryId: string | null }[] = [];
    let summary: string | null = null;
    let offset = 0;
    for (const part of parts) {
      const store = this.#storeOf(part.source) ?? this.#workspace;
      const release = part.source === WORKSPACE ? null : (this.#rooms?.open(part.source) ?? null);
      let result: DispatchResult;
      try {
        result = part.run(store);
      } finally {
        release?.();
      }
      if (!result.ok) {
        this.#rollBack(landed);
        const index = result.error.index;
        return {
          ok: false,
          error: { ...result.error, index: index === null ? null : index + offset },
        };
      }
      if (result.entryId !== null && summary === null) summary = store.getHistory().undoLabel;
      landed.push({ source: part.source, entryId: result.entryId });
      offset += part.size ?? 1;
    }

    const stacked = landed.filter((part): part is Part => part.entryId !== null);
    if (stacked.length > 0) {
      this.#undo.push({ parts: stacked, summary });
      if (this.#undo.length > HISTORY_LIMIT) this.#undo.shift();
      this.#redo.length = 0;
    }
    return {
      ok: true,
      state: this.getState(),
      entryId: stacked.at(-1)?.entryId ?? null,
    };
  }

  /**
   * Undo the parts of a batch that landed before a later part was refused.
   *
   * A part that stacked no entry cannot be taken back; such a command is not
   * undoable anywhere, and a batch that mixes one with a refusal is left as
   * the store would leave the part that ran.
   */
  #rollBack(landed: readonly { source: string; entryId: string | null }[]): void {
    for (const part of [...landed].reverse()) {
      if (part.entryId === null) continue;
      const store = this.#storeOf(part.source);
      if (store?.topUndoId() === part.entryId) store.undo();
    }
    // The rolled-back entries now sit on their stores' redo stacks, where
    // nothing here names them; a redo past them would be a redo of nothing
    // the brewer did.
    this.#redo.length = 0;
  }

  /** Whether every part of `entry` is still in its store. */
  #alive(entry: Entry): boolean {
    return entry.parts.every((part) => this.#storeOf(part.source)?.hasEntry(part.entryId) === true);
  }

  /**
   * What ⌘Z would undo: an entry of this stack, or a store's own entry that
   * sits above one. `prune` drops dead entries on the way (the read path
   * leaves them for the write path to drop).
   */
  #undoTarget(prune: boolean):
    | { readonly entry: Entry; readonly label: string | null }
    | {
        readonly entry: null;
        readonly store: WorkspaceStore;
        readonly source: string;
        readonly label: string | null;
      }
    | null {
    for (let index = this.#undo.length - 1; index >= 0; index -= 1) {
      const entry = this.#undo[index] as Entry;
      if (!this.#alive(entry)) {
        if (prune) this.#undo.splice(index, 1);
        continue;
      }
      const behind = entry.parts.find(
        (part) => this.#storeOf(part.source)?.topUndoId() !== part.entryId,
      );
      if (behind === undefined) return { entry, label: entry.summary };
      const store = this.#storeOf(behind.source) as WorkspaceStore;
      if (store.topUndoId() === null) {
        // Alive only on the redo side: already undone around this stack.
        if (prune) this.#undo.splice(index, 1);
        continue;
      }
      return {
        entry: null,
        store,
        source: behind.source,
        label: store.getHistory().undoLabel,
      };
    }
    // Nothing of this stack's is left. What the workspace still holds is from
    // before there were rooms, and is older than anything above.
    if (this.#workspace.topUndoId() === null) return null;
    return {
      entry: null,
      store: this.#workspace,
      source: WORKSPACE,
      label: this.#workspace.getHistory().undoLabel,
    };
  }

  /** What ⇧⌘Z would redo: the top entry whose every part its store would redo next. */
  #redoTarget(prune: boolean): { readonly entry: Entry; readonly label: string | null } | null {
    for (let index = this.#redo.length - 1; index >= 0; index -= 1) {
      const entry = this.#redo[index] as Entry;
      const ready =
        this.#alive(entry) &&
        entry.parts.every((part) => this.#storeOf(part.source)?.topRedoId() === part.entryId);
      if (ready) return { entry, label: entry.summary };
      if (prune) this.#redo.splice(index, 1);
    }
    return null;
  }

  /** A store's own result, with the merged state in place of the store's. */
  #result(result: DispatchResult | null): DispatchResult | null {
    if (result === null || !result.ok) return result;
    return { ...result, state: this.getState() };
  }
}
